import { Component, HostListener, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { OPTIONS_MASK } from 'src/app/core/constants/options-mask.constant';
import { ModalRoleEnum } from '../../enums/modal-role.enum';
import { CaixaService } from 'src/app/core/services/http/caixa.service';
import { map } from 'rxjs';
import { CaixaResponse } from '../../models/caixa-response';
import { KeyboardKeysEnum } from '../../enums/keyboard-keys.enum';
import { AuthenticationService } from 'src/app/core/services/http/authentication.service';
import { AberturaCaixaRequest } from '../../models/abertura-caixa-request';
import { ImpressaoTermicaService } from 'src/app/core/services/behavior/impressao-termica.service';
import { StorageService } from 'src/app/core/services/behavior/storage.service';
import { UsuarioModel } from '../../models/usuario.model';

@Component({
    selector: 'app-abertura-caixa-dialog',
    templateUrl: './abertura-caixa-dialog.component.html',
    styleUrls: ['./abertura-caixa-dialog.component.scss']
})
export class AberturaCaixaDialogComponent implements OnInit {
    public readonly dadosUltimoCaixa$ = this._caixaService.obterDadosUltimoCaixa(this._authService.numeroCaixa).pipe(map(l => l.success ? l.data : new CaixaResponse()));
    public readonly optionsMask = OPTIONS_MASK;

    public valor = 0;
    public aberturaConcluida = false;
    public isAbrindoCaixa = false;
    public mensagemAberturaFormatada = '';
    public exibirResumoAbertura = false;

    private _codigoUsuario = 0;

    constructor(
        private _modalCtrl: ModalController,

        private _caixaService: CaixaService,
        private _authService: AuthenticationService,
        private _impressaoTermicaService: ImpressaoTermicaService,
        private _storageService: StorageService
    ) { }

    public async cancelarAsyncOnClick() {
        await this._modalCtrl.dismiss(this.aberturaConcluida, ModalRoleEnum.CONFIRM);
    }

    public async confirmarAsyncOnClick() {
        await this._confirmarAsync();
    }

    public async ngOnInit() {
        this._codigoUsuario = this._authService.user?.codigo ?? 0;

        if (!this._codigoUsuario) {
            const usuarioLogado = await this._storageService.get('user') as UsuarioModel;
            this._codigoUsuario = usuarioLogado?.codigo ?? 0;
        }
    }

    @HostListener('window:keydown', ['$event'])
    public async _lidarComKeyboardEventAsync(event: KeyboardEvent): Promise<void> {
        if (event.key === KeyboardKeysEnum.Escape) {
            if (this.aberturaConcluida) {
                return;
            }

            await this._modalCtrl.dismiss(null, ModalRoleEnum.CANCEL);
            return;
        }

        const regex = /^[0-9]$/;

        if (regex.test(event.key)) {
            const input = document.getElementById("floatingInput");

            input.focus();
        } else if (event.key === KeyboardKeysEnum.F5) {
            event.preventDefault();
            event.stopPropagation();

            await this._confirmarAsync();
        }
    }

    private async _confirmarAsync() {
        if (this.aberturaConcluida) {
            await this._imprimirComprovanteEFecharAsync();
            return;
        }

        if (this.isAbrindoCaixa) {
            return;
        }

        const numeroCaixa = Number.parseInt(`${this._authService.numeroCaixa}`, 10);
        const request = new AberturaCaixaRequest(this._codigoUsuario, this.valor, numeroCaixa);

        this.isAbrindoCaixa = true;

        this._caixaService.abrirCaixa(request).subscribe(async res => {
            this.isAbrindoCaixa = false;

            if (!res.success) {
                return;
            }

            this.aberturaConcluida = true;
            this.mensagemAberturaFormatada = this._obterMensagemParaImpressao(res.data, res.message);
            this.exibirResumoAbertura = true;
        });
    }

    private async _imprimirComprovanteEFecharAsync(): Promise<void> {
        this._impressaoTermicaService.imprimirTexto(
            this.mensagemAberturaFormatada,
            'Abertura de caixa concluido com sucesso.',
            {
                incluirCabecalho: true,
                titulo: 'ABERTURA DO CAIXA'
            }
        );

        const dataClose = new AberturaCaixaDialogDataClose(this.valor);

        await this._modalCtrl.dismiss(dataClose, ModalRoleEnum.CONFIRM);
    }

    private _obterMensagemParaImpressao(data: any, message: string): string {
        if (typeof data === 'string' && data.trim().length > 0) {
            return data;
        }

        if (typeof message === 'string' && message.trim().length > 0) {
            return message;
        }

        return `\nValor de abertura: ${this._formatarValor(this.valor)}`;
    }

    private _formatarValor(valor: number): string {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor || 0);
    }
}

export class AberturaCaixaDialogDataClose {
    constructor(
        public valor: number
    ) { }
}
